import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { InsuranceCard } from '@/components/InsuranceCard.tsx';
import { Button } from '@/components/ui/button.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import type { PlanFamily, VerifiedInsurance } from '@/types/insurance.type.ts';

interface ResolveConfirmManagedProps {
  family: PlanFamily;
  familyName: string;
  managedName: string;
  insurance: VerifiedInsurance;
  showError?: boolean;
  onConfirm: () => void;
  onReject: () => void;
}

const formId = 'confirm-managed-form';
const confirmOptionSchema = z.enum(['yes', 'no']);
const formSchema = z.object({
  confirmOption: confirmOptionSchema.optional(),
});

type FormData = z.infer<typeof formSchema>;

export function ResolveConfirmManaged({
  managedName,
  insurance,
  showError,
  onConfirm,
  onReject,
}: ResolveConfirmManagedProps) {
  const { patientFirstName, patientLastName, patientDateOfBirth, patientGender, patientRecipient } =
    useSessionStore();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      confirmOption: undefined,
    },
  });
  const handleContinue = () => {
    const confirmOption = form.getValues('confirmOption');

    if (confirmOption === 'yes') {
      onConfirm();
    } else if (confirmOption === 'no') {
      onReject();
    } else {
      form.setError('confirmOption', { message: 'Please select whether this looks correct.' });
    }
  };

  return (
    <>
      <CardHeader>
        <CardTitle>We found your active {managedName} plan</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {showError && (
          <div className="rounded-md bg-rose-50 p-4 text-sm text-rose-700">
            We couldn't verify your insurance. Please check your information and try again.
          </div>
        )}

        <InsuranceCard
          insurance={insurance}
          policyHolder={{
            recipient: patientRecipient as 'me' | 'someone-else',
            firstName: patientFirstName,
            lastName: patientLastName,
            dateOfBirth: patientDateOfBirth,
            gender: patientGender as 'male' | 'female',
          }}
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleContinue();
          }}
          id={formId}
        >
          <FieldGroup className="space-y-6">
            <FieldSet>
              <FieldLegend>Does everything look correct?</FieldLegend>
              <Controller
                name="confirmOption"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <RadioGroup
                      value={field.value ?? ''}
                      onValueChange={(value) => {
                        field.onChange(confirmOptionSchema.parse(value));
                        form.clearErrors(field.name);
                      }}
                      orientation="horizontal"
                    >
                      <Field orientation="horizontal" className="w-auto">
                        <RadioGroupItem value="yes" id={`${formId}-yes`} />
                        <FieldLabel htmlFor={`${formId}-yes`}>Yes</FieldLabel>
                      </Field>

                      <Field orientation="horizontal" className="w-auto">
                        <RadioGroupItem value="no" id={`${formId}-no`} />
                        <FieldLabel htmlFor={`${formId}-no`}>No</FieldLabel>
                      </Field>
                    </RadioGroup>
                    <FieldError errors={[fieldState.error]} />
                  </>
                )}
              />
            </FieldSet>
          </FieldGroup>

          <div className="mt-8 w-full space-y-8 justify-self-center sm:w-auto sm:space-y-4.5 sm:justify-self-start">
            <Button className="w-full sm:w-auto sm:min-w-[186px]" type="submit">
              Continue
            </Button>
          </div>
        </form>
      </CardContent>
    </>
  );
}
